import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useBooking } from "../context/BookingContext";
import {
  applyFacilityBookingPricing,
} from "../utils/facilityEvents";

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests?: string;
}

const UserForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();
  const {
    userDetails,
    setUserDetails,
    selectedListing,
    selectedDate,
    startTime,
    endTime,
  } = useBooking();
  const { id } = location.state || {};
  const handleChange =
    (field: keyof UserFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setUserDetails({
        ...userDetails,
        [field]: e.target.value,
      });
    };

  const [errors] = useState<Partial<UserFormData>>({});

  // const validateForm = () => {
  //   const newErrors: Partial<UserFormData> = {};

  //   if (!userDetails.firstName.trim()) {
  //     newErrors.firstName = "First name is required";
  //   }
  //   if (!userDetails.lastName.trim()) {
  //     newErrors.lastName = "Last name is required";
  //   }
  //   if (!userDetails.email.trim()) {
  //     newErrors.email = "Email is required";
  //   } else if (!/\S+@\S+\.\S+/.test(userDetails.email)) {
  //     newErrors.email = "Please enter a valid email";
  //   }
  //   if (!userDetails.phone.trim()) {
  //     newErrors.phone = "Phone number is required";
  //   } else if (
  //     !/^\+?[\d\s-]{10,}$/.test(userDetails.phone.replace(/\s+/g, ""))
  //   ) {
  //     newErrors.phone = "Please enter a valid phone number";
  //   }

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

  const handleSubmit = () => {
    navigate(`/confirm-booking/${id}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!selectedDate || !selectedDate || !startTime || !endTime) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Missing booking information. Please start your booking again.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/listings")}
          startIcon={<ArrowBackIcon />}
        >
          Return to Listings
        </Button>
      </Container>
    );
  }

  //   const calculateDuration = () => {
  //     const start = parseInt(startTime.split(":")[0]);
  //     const end = parseInt(endTime.split(":")[0]);
  //     return end - start;
  //   };

  const calculateDuration = () => {
    if (!startTime || !endTime) return 0;

    // Parse hours and minutes separately
    const [startHourStr, startMinStr] = startTime.split(":");
    const [endHourStr, endMinStr] = endTime.split(":");

    const startHour = parseInt(startHourStr);
    const startMin = parseInt(startMinStr);
    const endHour = parseInt(endHourStr);
    const endMin = parseInt(endMinStr);

    const startTotalMinutes = startHour * 60 + startMin;
    const endTotalMinutes = endHour * 60 + endMin;

    const durationMinutes =
      endTotalMinutes > startTotalMinutes
        ? endTotalMinutes - startTotalMinutes
        : 0;
    return durationMinutes / 60;
  };

  const calculateCost = () => {
    const duration = calculateDuration();
    const subtotal =
      Math.round(duration * (selectedListing.pricePerHour || 0) * 100) / 100;
    return applyFacilityBookingPricing(subtotal, selectedDate);
  };

  const steps = ["Select Date & Time", "Guest Information", "Confirm Booking"];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header with Back Button and Stepper */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton
            onClick={handleBack}
            sx={{ mr: 2 }}
            aria-label="Go back"
            color="primary"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            Guest Information
          </Typography>
        </Box>

        <Stepper
          activeStep={1}
          alternativeLabel={!isMobile}
          orientation={isMobile ? "horizontal" : "horizontal"}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{isMobile ? "" : label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* Guest Information Form */}
        <Paper
          elevation={3}
          sx={{
            flex: 2,
            p: 3,
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Personal Details
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please provide your information to complete the booking.
            </Typography>
          </Box>

          <Stack spacing={3}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={userDetails.firstName}
                onChange={handleChange("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={userDetails.lastName}
                onChange={handleChange("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={userDetails.email}
              onChange={handleChange("email")}
              error={!!errors.email}
              helperText={errors.email}
              required
              InputProps={{
                startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
              }}
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={userDetails.phone}
              onChange={handleChange("phone")}
              error={!!errors.phone}
              helperText={errors.phone}
              required
              InputProps={{
                startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
              }}
            />

            <TextField
              fullWidth
              label="Special Requests (Optional)"
              multiline
              rows={3}
              value={userDetails.specialRequests}
              onChange={handleChange("specialRequests")}
              placeholder="Any special requirements or requests..."
            />
          </Stack>
        </Paper>

        {/* Booking Summary */}
        <Paper
          elevation={3}
          sx={{
            flex: 1,
            p: 3,
            borderRadius: 2,
            bgcolor: "background.paper",
            height: "fit-content",
          }}
        >
          <Typography variant="h6" color="primary" gutterBottom>
            Booking Summary
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                <EventIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Date
              </Typography>
              <Typography variant="body1">
                {new Date(selectedDate).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                <AccessTimeIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Time
              </Typography>
              <Typography variant="body1">
                {startTime} - {endTime}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                <LocationOnIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Location
              </Typography>
              <Typography variant="body1">
                50 Neptune Crescent Unit 1, Dartmouth, NS B2Y 0B6
              </Typography>
            </Box>

            <Divider />

            <Box
              sx={{
                p: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderRadius: 1,
              }}
            >
              {(() => {
                const pricing = calculateCost();
                const showBreakdown =
                  pricing.promoActive || pricing.taxWaived;
                return (
                  <>
                    {pricing.notices.map((event) => (
                      <Typography
                        key={event.id}
                        variant="body2"
                        sx={{ mb: 1, opacity: 0.95 }}
                      >
                        {event.bookingLabel}
                        {pricing.promoActive &&
                        (event.bookingPercentOff ?? 0) > 0
                          ? ` — save $${pricing.discountAmount.toFixed(2)}`
                          : ""}
                      </Typography>
                    ))}
                    {pricing.promoActive && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                          opacity: 0.9,
                        }}
                      >
                        <Typography variant="body2">Subtotal</Typography>
                        <Typography variant="body2">
                          ${pricing.subtotal.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    {pricing.promoActive && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          Discount ({pricing.discountPercent}% off)
                        </Typography>
                        <Typography variant="body2">
                          −${pricing.discountAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    {pricing.taxWaived && (
                      <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.95 }}>
                        HST waived at checkout
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="subtitle1">
                        {showBreakdown ? "You pay" : "Total Cost"}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AttachMoneyIcon sx={{ mr: 0.5 }} />
                        <Typography variant="h6" fontWeight="bold">
                          {pricing.total.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* Continue Button */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1.1rem",
          }}
        >
          Continue to Confirmation
        </Button>
      </Box>
    </Container>
  );
};

export default UserForm;
